import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import {
  ERRCODE_SUCCESS,
  ERRCODES_COMMON,
  ERRCODES_PURCHASE_VALID_CHECK,
} from 'src/service/constant/errcodes'
import { DateUtils } from 'src/utils/date'
import {
  FavoriteStarSpace,
  PurchaseValidCheckSpace,
  ResponseDataType,
  SelfRegisterCouponUpdateSpace,
  WalletCouponSpace,
  WalletImminentCouponSpace,
} from 'src/types/api'
import {
  useCustomQuery,
  walletCouponUsedCheckQueryOption,
  walletImminentCouponQueryOption,
} from 'src/hooks/react-query/useCustomQuery.hook'
import MainBottomSheetView from 'src/components/layout/MainLayout/MainBottomSheet/view'
import usePageNav from 'src/hooks/usePageNav.hook'
import {
  favoriteUpdateQueryOption,
  purchaseValidCheckQueryOption,
  selfRegisterCouponUpdateQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import { bottomSheetSortType } from 'src/components/layout/MainLayout/MainBottomSheet/_components/MainBottomSheetHeader'
import { useModal } from 'src/provider/ModalContextProvider'

import { queryClient } from 'src/router'
import {
  useInfiniteScrollQuery,
  walletCouponQueryOption,
} from 'src/hooks/react-query/useCustomInfiniteQuery.hook'
import useLockBodyScroll from 'src/hooks/useLockBodyScroll.hook'
import { useBlocker } from 'react-router-dom'
import { handleJsonParse } from 'src/utils'
import { useGlobalStore } from 'src/store/zustand'
import { addFlowBreadcrumb } from 'src/utils/sentry/breadcrumbs'

export type ActiveSwiperDataType = {
  barcode?: string
  btnTxt?: string
  func?: () => void
  favorite?: 0 | 1
  chipsTxt?: string
}

/** 카드별 칩 텍스트 (각자 CouponType 기준 표시) */
export const getWalletCouponChipsTxt = (
  couponType?: 0 | 1 | 2,
  couponCnt?: number,
): string | undefined => {
  if (couponType === 0) {
    return (couponCnt ?? 0) <= 1 ? undefined : `${couponCnt} 보유중`
  }
  if (couponType === 1) return '핫딜쿠폰'
  if (couponType === 2) return '직접등록'
  return undefined
}

export interface BottomSheetProps {
  isBottomSheetOpen: boolean
  bottomSheetToggle: () => void
  walletCouponData?: WalletCouponSpace.CouponListInterface[]
  bottomSheetCouponSortHandler: (value: bottomSheetSortType) => void
  couponSortOrder: bottomSheetSortType
  walletCouponDataNoMore: boolean
  setTarget: Dispatch<SetStateAction<HTMLDivElement | null | undefined>>
  closedBottomSheetContents: {
    chips: { text: string; color: string }
    primary: string
    button: { text: string; func: () => void }
  }
  activeSwiperHandler: ActiveSwiperDataType
  couponPurchaseHistoryPageNav: () => void
  setSwiperActiveIdxHandler: (activeIdx: number) => void
  isWalletCouponDataPending?: boolean
  bottomSheetHeaderReloadHandler: () => void
  purchaseHistoryDetailPageNav: (
    Idx: number,
    couponIdx: number,
    couponStatus: string,
    couponType: number,
    couponNumber: string,
  ) => void
  favoriteUpdateHandler: () => void
  selfRegisterListPageNav: (replace: boolean) => void
}

const MainBottomSheet = () => {
  const [couponSortOrder, setCouponSortOrder] = useState<bottomSheetSortType>(0)
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false)
  const [swiperActiveIdx, setSwiperActiveIdx] = useState(0)
  const { openModal } = useModal()
  const { maintenanceBlock, setIsDisplayLightUp } = useGlobalStore()
  let blocker = useBlocker(isBottomSheetOpen)

  const { DateFormat, getDayDifference, expiration7Days } = DateUtils

  const {
    purchasePageNav,
    couponPurchaseHistoryPageNav,
    purchaseHistoryDetailPageNav,
    selfRegisterListPageNav,
    couponDetailPageNav,
  } = usePageNav()

  const { mutateAsync: purchaseValidCheckMutate } = useCustomMutation<
    PurchaseValidCheckSpace.Request,
    PurchaseValidCheckSpace.Response
  >(purchaseValidCheckQueryOption())

  const { mutateAsync: favoriteUpdateMutate } = useCustomMutation<
    FavoriteStarSpace.Request,
    FavoriteStarSpace.Response
  >(favoriteUpdateQueryOption())

  const { mutateAsync: selfUpdateMutate } = useCustomMutation<
    SelfRegisterCouponUpdateSpace.Request,
    SelfRegisterCouponUpdateSpace.Response
  >(selfRegisterCouponUpdateQueryOption())

  const {
    data: nearestExpireQueryData,
    isSuccess: isNearestExpireSuccess,
    refetch: nearExpireCouponRefetch,
  } = useCustomQuery<WalletImminentCouponSpace.Response>(walletImminentCouponQueryOption())

  const {
    data: walletCouponListQueryData,
    isPending: isWalletCouponDataPending,
    setTarget,
    refetch: walletCouponDataReFetch,
    isSuccess: isWalletCouponDataSuccess,
  } = useInfiniteScrollQuery<WalletCouponSpace.Request, WalletCouponSpace.Response>(
    walletCouponQueryOption(couponSortOrder),
  )

  const walletCouponListProcessData = useCallback(() => {
    let result: WalletCouponSpace.CouponListInterface[] | any[] = []
    walletCouponListQueryData?.pages?.map(
      // eslint-disable-next-line array-callback-return
      (element: ResponseDataType<WalletCouponSpace.Response>) => {
        !!element?.Body?.CouponList && result.push(...element?.Body?.CouponList)
      },
    )

    return {
      result,
      noMore: walletCouponListQueryData?.pages?.at(-1)?.Body?.CouponList?.length === 0,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletCouponListQueryData, isWalletCouponDataSuccess])

  const activeSwiperItems = walletCouponListProcessData().result?.at(swiperActiveIdx)

  const { data: walletCouponCheckQueryData, isSuccess: isWalletCouponUsedCheckSuccess } =
    useCustomQuery(
      walletCouponUsedCheckQueryOption(
        { CouponOrderID: `${activeSwiperItems?.CouponOrderID}` },
        isBottomSheetOpen && !!activeSwiperItems?.CouponOrderID,
      ),
    )

  const bottomSheetToggle = () => {
    if (!!maintenanceBlock) {
      openModal({
        contents: { t: maintenanceBlock.t, d: maintenanceBlock.d },
        button: { submit: { t: '확인' } },
      })
      return
    }
    if (!isBottomSheetOpen) {
      walletCouponDataReFetch?.()
      setIsDisplayLightUp(true)
    } else {
      setCouponSortOrder(0)
      setIsDisplayLightUp(false)
      setSwiperActiveIdx(0)
    }

    setIsBottomSheetOpen((prevState) => !prevState)
  }

  const bottomSheetHeaderReloadHandler = () => {
    queryClient.removeQueries({ queryKey: ['wallet_coupon_list'] })
    walletCouponDataReFetch?.()
    setSwiperActiveIdx(0) //swiperActive Idx 초기화
  }

  const bottomSheetCouponSortHandler = (value: bottomSheetSortType) => {
    queryClient.removeQueries({ queryKey: ['wallet_coupon_list'] })
    setCouponSortOrder(value)
    setSwiperActiveIdx(0) //swiperActive Idx 초기화
  }

  const setSwiperActiveIdxHandler = (activeIdx: number) => setSwiperActiveIdx(activeIdx)

  const blockPageNavHandler = async () => {
    setIsBottomSheetOpen(false)
    blocker.reset?.()
  }

  const favoriteUpdateHandler = () => {
    const register = activeSwiperItems?.Favorite === 0 // 현재 미등록 상태
    const alwaysCoupon = activeSwiperItems?.CouponType === 0
    const unPurchasedCoupon = activeSwiperItems?.ExpireDT === ''

    if (!alwaysCoupon) {
      return
    }

    const favoriteUpdate = () => {
      favoriteUpdateMutate({
        CouponNumber: activeSwiperItems?.CouponNumber as string,
        CouponIdx: activeSwiperItems?.CouponIdx as number,
        Type: activeSwiperItems?.Favorite as 0 | 1,
      }).then(({ Header }) => {
        if (Header.ErrCode === ERRCODE_SUCCESS) {
          walletCouponDataReFetch?.()
        }
      })
    }

    if (!register && unPurchasedCoupon) {
      const t = '미보유 쿠폰 삭제'
      const d = '미보유 쿠폰을 즐겨찾기 취소하면 쿠폰지갑에서 삭제됩니다.'
      openModal({
        contents: { t, d },
        button: { submit: { t: '삭제', f: favoriteUpdate }, cancel: { t: '취소' } },
      })
    } else favoriteUpdate()
  }

  const closedWalletStatusData = useCallback(() => {
    switch (true) {
      case !!nearestExpireQueryData?.Body?.CouponName:
        const { ExpireDT, CouponName } = nearestExpireQueryData?.Body!
        const remainingDate = getDayDifference(ExpireDT as string, 'days')
        return {
          chips: {
            text: `D-${remainingDate}`,
            color: expiration7Days(ExpireDT) ? 'var(--black-20)' : 'var(--red-30)',
          },
          primary: CouponName as string,
          button: { text: '쿠폰사용', func: bottomSheetToggle },
        }
      case !isNearestExpireSuccess:
      default:
        return {
          chips: { text: '미보유', color: 'var(--black-20)' },
          primary: '쿠폰을 채워주세요',
          button: { text: '쿠폰사용', func: bottomSheetToggle },
        }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNearestExpireSuccess, nearestExpireQueryData, maintenanceBlock])

  const couponUsageCompleteHandler = (activeSwiperItems: WalletCouponSpace.CouponListInterface) => {
    const { Memo, Barcode, CouponImage, CouponName, BrandName, ExpireDT, Idx } = activeSwiperItems
    const submitData = {
      Barcode,
      Memo,
      CouponName,
      Idx,
      CouponImageURL: CouponImage,
      BrandName,
      ExpireDT: DateFormat(ExpireDT, 'YYYY-MM-DD'),
      UseFlag: 1,
    }

    const handleSelfRegCouponUpdate = () =>
      selfUpdateMutate(submitData).then(() => {
        walletCouponDataReFetch?.()
        nearExpireCouponRefetch?.()
      })

    openModal({
      contents: {
        t: '직접등록쿠폰 사용완료',
        d: `해당 쿠폰을 사용완료하면 \n '마이페이지 - 직접등록한 쿠폰'\n에서만 확인할 수 있습니다.`,
      },
      button: { submit: { t: '사용완료', f: handleSelfRegCouponUpdate }, cancel: { t: '취소' } },
    })
  }

  const purchaseValidCheckHandler = ({
    CouponIdx,
    CouponType,
    CouponNumber,
    SalePrice,
  }: PurchaseValidCheckSpace.Request & { CouponNumber: string }) => {
    const submitData = {
      CouponIdx: CouponIdx,
      CouponType: CouponType,
      SalePrice: SalePrice,
    }
    addFlowBreadcrumb('payment_attempt', 'request', { handler: 'purchaseValidCheckHandler' })
    purchaseValidCheckMutate(submitData).then(({ Header, Body }) => {
      if (Header.ErrCode === ERRCODE_SUCCESS) {
        blockPageNavHandler().then(() => {
          setTimeout(() => {
            purchasePageNav(CouponType, CouponIdx, CouponNumber)
          }, 200)
        })
      } else {
        addFlowBreadcrumb('payment_fail', 'response', {
          handler: 'purchaseValidCheckHandler',
          errCode: Header.ErrCode,
        })
        const ErrMsg = handleJsonParse(Header.ErrMsg)
        const { Title = '', Contents = '' } = ErrMsg
        switch (Header.ErrCode) {
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_NOT_VALID_COUPON:
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_HOT_DEAL_SOLD_OUT:
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_ONLY_ONE_HOTDEAL_PURCHASE:
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_DAILY_PURCHASED:
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '확인' } },
            })
            break
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_DIFFERENT_PRICE:
            const handleDifferentPriceSubmit = () => {
              blockPageNavHandler().then(() => {
                couponDetailPageNav(CouponNumber, CouponIdx, CouponType)
              })
            }
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '확인', f: handleDifferentPriceSubmit } },
            })
            break
          case ERRCODES_PURCHASE_VALID_CHECK.ERROR_MAX_OF_MONTH_HOTDEAL_PURCHASE:
            openModal({
              contents: { t: Title, d: Contents },
              button: { submit: { t: '닫기' } },
            })
            break
          //통신 모듈에 있는 에러 핸들링 사용
          case ERRCODES_COMMON.PIN_CHECK_ERROR:
          case ERRCODES_COMMON.SESSION_ERROR:
          case ERRCODES_COMMON.ERROR_LOGOUT:
            break
          default:
            openModal({ contents: { d: Header.ErrMsg }, button: { submit: { t: '닫기' } } })
            break
        }
      }
    })
  }

  const activeSwiperHandler = useCallback(() => {
    const activeSwiperData: { [key: number]: ActiveSwiperDataType } = {
      0: {
        barcode: activeSwiperItems?.Barcode,
        func: () =>
          purchaseValidCheckHandler({
            CouponIdx: activeSwiperItems?.CouponIdx as number,
            CouponNumber: activeSwiperItems?.CouponNumber as string,
            CouponType: activeSwiperItems?.CouponType as 0 | 1,
            SalePrice: activeSwiperItems?.SalePrice as number,
          }),
        btnTxt: '쿠폰 추가구매',
        favorite: activeSwiperItems?.Favorite,
        chipsTxt: getWalletCouponChipsTxt(0, activeSwiperItems?.CouponCnt),
      },
      1: {
        barcode: activeSwiperItems?.Barcode,
        func: undefined,
        btnTxt: undefined,
        favorite: undefined,
        chipsTxt: getWalletCouponChipsTxt(1),
      },
      2: {
        barcode: activeSwiperItems?.Barcode,
        func: () => couponUsageCompleteHandler(activeSwiperItems!),
        btnTxt: '사용완료 처리하기',
        favorite: undefined,
        chipsTxt: getWalletCouponChipsTxt(2),
      },
    }
    return activeSwiperData[activeSwiperItems?.CouponType as 0 | 1 | 2]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeSwiperItems,
    purchaseValidCheckHandler,
    couponUsageCompleteHandler,
    isWalletCouponDataSuccess,
  ])

  useLockBodyScroll(isBottomSheetOpen)

  useEffect(() => {
    if (isWalletCouponUsedCheckSuccess) {
      walletCouponCheckQueryData?.Body?.Use === 'Y' &&
        queryClient.removeQueries({ queryKey: ['wallet_coupon_list'] })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWalletCouponUsedCheckSuccess])

  useEffect(() => {
    const popStateHandler = () => {
      setCouponSortOrder(0)
      setIsDisplayLightUp(false)
      setIsBottomSheetOpen(false)
    }

    window.addEventListener('popstate', popStateHandler)
    return () => {
      window.removeEventListener('popstate', popStateHandler)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isBottomSheetOpen) {
      blocker.state === 'blocked' && blocker.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBottomSheetOpen])

  const props: BottomSheetProps = {
    closedBottomSheetContents: closedWalletStatusData(),
    isBottomSheetOpen,
    bottomSheetCouponSortHandler,
    bottomSheetToggle,
    couponSortOrder,
    walletCouponData: walletCouponListProcessData()?.result,
    walletCouponDataNoMore: walletCouponListProcessData().noMore,
    activeSwiperHandler: activeSwiperHandler(),
    setSwiperActiveIdxHandler,
    bottomSheetHeaderReloadHandler,
    favoriteUpdateHandler,
    isWalletCouponDataPending,
    setTarget,
    purchaseHistoryDetailPageNav: (
      Idx: number,
      couponIdx: number,
      couponStatus: string,
      couponType: number,
      couponNumber: string,
    ) => {
      blockPageNavHandler().then(() => {
        purchaseHistoryDetailPageNav(Idx, couponIdx, couponStatus, couponType, couponNumber)
      })
    },
    couponPurchaseHistoryPageNav: () => {
      blockPageNavHandler().then(() => {
        couponPurchaseHistoryPageNav()
      })
    },
    selfRegisterListPageNav: () => {
      blockPageNavHandler().then(() => {
        selfRegisterListPageNav(false)
      })
    },
  }

  return <MainBottomSheetView {...props} />
}
export default MainBottomSheet
